/**
 * CommandSearch — what the app can do, findable by typing.
 *
 * The third reader of `lib/actions.ts`. The UI already has every one of these
 * behind a key or a chip; this is the surface that answers "what *can* it do",
 * which nothing on screen does — the same gap the indicator picker's search
 * closes for indicators.
 *
 * ## It cannot open on a bare letter, and that is not a preference
 *
 * `use-type-to-search.ts` claims every unmodified letter, app-wide and
 * unconditionally, for the symbol box. So the palette opens on **Ctrl/⌘+K** —
 * `type-to-search.ts` returns `null` for anything with a modifier, so the two
 * listeners cannot fight over a keystroke.
 *
 * **Ctrl+Q opens it too** (owner, 2026-10-10: Q for QuFolio), Ctrl and never ⌘ —
 * ⌘Q quits the browser on a Mac and no page can stop it. Ctrl+K stays, so a
 * hand that reaches for the usual key is not punished. The tab bar's search box
 * is a third way in, for whoever knows neither.
 *
 * ## A refusal keeps the palette open
 *
 * Picking a command runs it, and a run can refuse — "not on a three-chart
 * layout", "no timeframe above this one". Closing on a refusal would leave the
 * reason nowhere: the user asked for something, the palette vanished, and the
 * screen did not change. So a refusal is shown **in place** and the palette
 * stays, which is also where the reason is worth reading.
 *
 * ## Rows are commands, not actions
 *
 * "Show peers" rather than "change what the picture-in-picture shows", with the
 * argument already chosen. `lib/command-palette.ts` derives them from the
 * registry's own schemas, so the list cannot drift from what the agent can call.
 *
 * ## A second step, in the same box
 *
 * A row ending in `…` asks one more thing before it runs (owner's mock,
 * 2026-10-18). **Add indicator…** turns the box into `Add indicator:` over the
 * catalog; **Paste symbols…** into `Paste into:` over the user's lists when no
 * list is on screen; **Describe a column… / condition…** into a text box. ←, or
 * Backspace on an empty box, goes back; Esc closes from any step. A describe
 * is a billed model call, so it is asked on **Enter only**, never per
 * keystroke, and the answer's [Add] / [Edit] are what run.
 *
 * ## Typing a ticker, an interval, or a question
 *
 * `avgo` offers **Go to AVGO** (and `AVGO/SPY`) above the commands; `1w` offers
 * **Interval — 1 week**; `atr 5 sma 20` offers **Add indicator — ATR (5) with
 * SMA (20)** and its "separately" reading, here and under `Add indicator:`; and whatever is typed ends in **Ask AI: "…"** where
 * the chat is offered — `topCommands` in `lib/command-palette.ts`.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import { loadGuideIndex } from "../api/guide-index";
import { loadIndicatorCatalog } from "../api/indicators";
import type { Action, ActionContext, AskOutcome } from "../lib/actions";
import {
  browseCommands,
  browseHits,
  emptyQueryCommands,
  paletteCommands,
  topCommands,
  type Command,
} from "../lib/command-palette";
import { useSymbolIndex } from "../lib/symbol-index";
import { QueVeeMark } from "./QueVeeMark";

export interface CommandSearchProps {
  ctx: ActionContext;
  onClose: () => void;
}

/**
 * Which step the box is on. `top` is the command list; the other two are the
 * second question a `palette` row asks — in the same box, so the person never
 * leaves the place they typed into, and ← or Backspace on an empty box goes back.
 */
type Step = { kind: "top" } | { kind: "browse"; action: Action } | { kind: "text"; action: Action };

export function CommandSearch({ ctx, onClose }: CommandSearchProps) {
  const [step, setStep] = useState<Step>({ kind: "top" });
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const [refusal, setRefusal] = useState<string | null>(null);
  /** A text step's answer, and the text it answered — Enter on the same text does not ask twice. */
  const [answer, setAnswer] = useState<{ text: string; outcome: AskOutcome } | null>(null);
  const [asking, setAsking] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // The indicator catalog is fetched once per page; asking here means a palette
  // opened before the picker ever was still gets its Add indicator rows, and the
  // bump re-derives the list when it lands.
  const [catalogLoaded, setCatalogLoaded] = useState(0);
  useEffect(() => {
    let alive = true;
    void loadIndicatorCatalog().then(() => alive && setCatalogLoaded((n) => n + 1));
    return () => {
      alive = false;
    };
  }, []);

  // The user guide, for the Guide rows — fetched once per page, same origin.
  // A failed read leaves no Guide rows and nothing else changes.
  const [guideLoaded, setGuideLoaded] = useState(0);
  useEffect(() => {
    let alive = true;
    void loadGuideIndex()
      .then(() => alive && setGuideLoaded((n) => n + 1))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  // Derived per catalog load, not per keystroke: rebuilding the expansion while
  // somebody types would re-key every row under them.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const all = useMemo(() => paletteCommands(undefined, ctx), [ctx, catalogLoaded]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const choices = useMemo(
    () => (step.kind === "browse" ? browseCommands(step.action, ctx) : []),
    [step, ctx, catalogLoaded],
  );
  // The symbol index for Go to, asked for on the first keystroke rather than
  // on open — it is 2.7MB, and a palette opened to export a chart never needs
  // it. Its arrival re-derives the rows.
  const symbolsLanded = useSymbolIndex(query.trim() !== "");

  // An empty box lists the commands, not a row per indicator: two hundred
  // "Add indicator — …" rows would bury the dozen everything else is. The
  // `Add indicator…` row opens them; typing `macd` still finds one directly.
  // A typed query adds Go to, Interval and Ask AI around the matches.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const hits = useMemo(() => {
    if (step.kind === "text") return [];
    if (step.kind === "browse") return browseHits(query, step.action, choices, ctx);
    return query.trim() ? topCommands(query, all, ctx) : emptyQueryCommands(all);
  }, [step, query, all, choices, ctx, symbolsLanded, guideLoaded]);

  // The cursor is an index into a list that changes under it. Clamped rather
  // than reset, so refining a query keeps the pick where it was when the row is
  // still there.
  const at = Math.min(cursor, Math.max(0, hits.length - 1));

  useEffect(() => setRefusal(null), [query, step]);

  // Keep the highlighted row in view when the cursor is moved by keyboard —
  // arrowing past the fold otherwise moves a selection nobody can see.
  useEffect(() => {
    listRef.current?.querySelector('[data-at="1"]')?.scrollIntoView?.({ block: "nearest" });
  }, [at, hits]);

  const goTo = (next: Step) => {
    setStep(next);
    setQuery("");
    setCursor(0);
    setAnswer(null);
    inputRef.current?.focus();
  };

  /** Run an action; close on success, keep the reason on screen on a refusal. */
  const run = (action: Action, args: Record<string, unknown>) => {
    const outcome = action.run(ctx, args);
    if (outcome.ok) {
      onClose();
      return;
    }
    // Stays open, with the reason where the question was asked.
    setRefusal(outcome.reason);
  };

  const runAt = (index: number) => {
    const command = hits[index];
    if (!command) return;
    if (command.enter) {
      goTo({ kind: command.enter, action: command.action });
      return;
    }
    run(command.action, command.args);
  };

  /**
   * Enter in a text step. **Only here** — an `ask` may be a model call, billed
   * per call, so nothing about typing asks anything, and Enter again on the
   * text already answered does not ask twice.
   */
  const ask = () => {
    if (step.kind !== "text" || !step.action.ask || asking) return;
    const text = query.trim();
    // A failed answer may be retried as typed — a dropped connection is not the
    // model's last word; a good one is not paid for twice.
    if (!text || (answer?.text === text && answer.outcome.ok)) return;
    const action = step.action;
    setAsking(true);
    setRefusal(null);
    void action
      .ask!(ctx, text)
      .then((outcome) => setAnswer({ text, outcome }))
      .catch((cause: unknown) =>
        setAnswer({ text, outcome: { ok: false, reason: cause instanceof Error ? cause.message : String(cause) } }),
      )
      .finally(() => setAsking(false));
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
    } else if (event.key === "Backspace" && step.kind !== "top" && query === "") {
      event.preventDefault();
      goTo({ kind: "top" });
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      setCursor(Math.min(at + 1, hits.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setCursor(Math.max(at - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (step.kind === "text") ask();
      else runAt(at);
    }
  };

  const prompt = step.kind === "top" ? null : (step.action.palette?.prompt ?? step.action.title);
  const placeholder = step.kind === "top" ? "Search or type a command" : (step.action.palette?.placeholder ?? "");

  return (
    <>
      {/* The scrim closes on a click and is what makes "click away" work without
          a document listener that would also hear the opening keystroke. */}
      <div style={SCRIM} onPointerDown={onClose} />
      <div style={HOST} role="dialog" aria-modal="true" aria-label="Find a command">
        <div style={BAR}>
          {prompt && (
            <>
              <button
                type="button"
                aria-label="Back to commands"
                title="Back (Backspace on an empty box)"
                onPointerDown={(e) => {
                  // Kept off the input's blur, so focus stays in the box.
                  e.preventDefault();
                  goTo({ kind: "top" });
                }}
                style={BACK}
              >
                ←
              </button>
              <span style={PROMPT}>{prompt}</span>
            </>
          )}
          <input
            ref={inputRef}
            autoFocus
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setCursor(0);
            }}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            aria-label={prompt ?? "Find a command"}
            aria-controls="qf-command-list"
            style={INPUT}
          />
        </div>
        {refusal && (
          <div role="status" style={REFUSAL}>
            {refusal}
          </div>
        )}
        {step.kind === "text" ? (
          <TextStep asking={asking} answer={answer?.outcome ?? null} onOffer={(args) => run(step.action, args)} />
        ) : (
          <div id="qf-command-list" ref={listRef} role="listbox" style={LIST}>
            {hits.length === 0 ? (
              <div style={EMPTY}>
                {query ? <>nothing matches “{query}”</> : step.kind === "browse" ? "nothing to pick yet" : null}
              </div>
            ) : (
              hits.map((command, index) => (
                <Row
                  key={command.key}
                  command={command}
                  selected={index === at}
                  onPick={() => runAt(index)}
                  onHover={() => setCursor(index)}
                />
              ))
            )}
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Under a text step's box: nothing until Enter, then the answer — the compiled
 * DSL with a button per way to use it, or the reason there is none.
 */
function TextStep({
  asking,
  answer,
  onOffer,
}: {
  asking: boolean;
  answer: AskOutcome | null;
  onOffer: (args: Record<string, unknown>) => void;
}) {
  if (asking) return <div style={EMPTY}>Describing…</div>;
  if (!answer) return <div style={EMPTY}>Press Enter to describe it — one AI call each time.</div>;
  if (!answer.ok) {
    return (
      <div role="status" style={REFUSAL}>
        {answer.reason}
      </div>
    );
  }
  return (
    <div style={ANSWER}>
      <code style={DSL}>{answer.preview}</code>
      <div style={OFFERS}>
        {answer.offers.map((offer) => (
          <button
            key={offer.label}
            type="button"
            // `pointerdown`, for the Row's reason: the scrim hears one too.
            onPointerDown={(e) => {
              e.stopPropagation();
              onOffer(offer.args);
            }}
            style={OFFER}
          >
            {offer.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Row({
  command,
  selected,
  onPick,
  onHover,
}: {
  command: Command;
  selected: boolean;
  onPick: () => void;
  onHover: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      data-at={selected ? "1" : "0"}
      // `pointerdown` rather than click: the scrim behind also listens for one,
      // and a click fires after the scrim has already closed the palette.
      onPointerDown={(e) => {
        e.stopPropagation();
        onPick();
      }}
      onPointerEnter={onHover}
      style={{ ...ROW, ...(selected ? ROW_ON : {}) }}
    >
      <span style={ROW_TITLE}>
        {command.key === "askAi" && <QueVeeMark size={18} style={{ marginRight: 6, verticalAlign: "-4px" }} />}
        {command.title}
      </span>
      <span style={ROW_DESC}>{command.subtitle ?? command.action.description}</span>
    </button>
  );
}

const SCRIM: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 1999,
  background: "rgba(0,0,0,0.35)",
};

const HOST: React.CSSProperties = {
  position: "fixed",
  // Near the top rather than centred: the list grows downward, and a box that
  // grows from the middle of the screen moves its own input while you type.
  top: "12vh",
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 2000,
  width: "min(560px, calc(100vw - 32px))",
  maxHeight: "70vh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  borderRadius: 8,
  border: "1px solid var(--qf-border)",
  background: "var(--qf-surface)",
  boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
};

const BAR: React.CSSProperties = {
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  borderBottom: "1px solid var(--qf-border)",
};

const BACK: React.CSSProperties = {
  flex: "0 0 auto",
  marginLeft: 8,
  padding: "2px 6px",
  border: "none",
  borderRadius: 4,
  background: "transparent",
  color: "var(--qf-text-muted)",
  font: "inherit",
  fontSize: 15,
  cursor: "pointer",
};

const PROMPT: React.CSSProperties = {
  flex: "0 0 auto",
  paddingLeft: 4,
  fontSize: 15,
  color: "var(--qf-text-muted)",
  whiteSpace: "nowrap",
};

const ANSWER: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  padding: "10px 14px 12px",
};

const DSL: React.CSSProperties = {
  display: "block",
  padding: "6px 8px",
  borderRadius: 4,
  background: "var(--qf-surface-alt)",
  fontFamily: "ui-monospace, Menlo, Consolas, monospace",
  fontSize: 12,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const OFFERS: React.CSSProperties = { display: "flex", gap: 6, justifyContent: "flex-end" };

const OFFER: React.CSSProperties = {
  padding: "4px 12px",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  background: "var(--qf-background)",
  color: "var(--qf-text)",
  font: "inherit",
  fontSize: 12,
  cursor: "pointer",
};

const INPUT: React.CSSProperties = {
  flex: "1 1 auto",
  minWidth: 0,
  padding: "12px 14px",
  border: "none",
  background: "transparent",
  color: "var(--qf-text)",
  font: "inherit",
  fontSize: 15,
  outline: "none",
};

const REFUSAL: React.CSSProperties = {
  flex: "0 0 auto",
  padding: "8px 14px",
  fontSize: 12,
  lineHeight: 1.5,
  color: "var(--qf-warning)",
  background: "var(--qf-surface-alt)",
  borderBottom: "1px solid var(--qf-border)",
};

const LIST: React.CSSProperties = { flex: 1, minHeight: 0, overflowY: "auto", padding: 4 };

const ROW: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  width: "100%",
  padding: "7px 10px",
  border: "none",
  borderRadius: 5,
  background: "transparent",
  color: "var(--qf-text)",
  font: "inherit",
  textAlign: "left",
  cursor: "pointer",
};

const ROW_ON: React.CSSProperties = { background: "var(--qf-selection)" };

const ROW_TITLE: React.CSSProperties = { fontSize: 13 };

const ROW_DESC: React.CSSProperties = {
  fontSize: 11,
  lineHeight: 1.4,
  color: "var(--qf-text-muted)",
  overflow: "hidden",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
};

const EMPTY: React.CSSProperties = {
  padding: "18px 14px",
  textAlign: "center",
  fontSize: 12,
  color: "var(--qf-text-muted)",
};
